import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Sparkles,
  RotateCcw,
  ExternalLink,
  Upload,
  X,
  Copy,
} from "lucide-react";
import { toast } from "sonner";

const RECOMMENDED_WIDTH = 480;
const RECOMMENDED_HEIGHT = 270;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024; // 2 MB

export const Route = createFileRoute("/_app/admin/ads")({
  component: AdminAdsPage,
});

type SidebarAd = {
  id: string;
  title: string;
  eyebrow: string | null;
  cta_text: string | null;
  target_url: string;
  image_url: string | null;
  gradient: string | null;
  display_seconds: number;
  max_impressions: number | null;
  impressions_count: number;
  clicks_count: number;
  is_active: boolean;
  sort_order: number;
  text_overlay: boolean;
  overlay_opacity: number;
  text_color: string;
  created_at: string;
  updated_at: string;
};

type AdFormState = {
  title: string;
  eyebrow: string;
  cta_text: string;
  target_url: string;
  image_url: string;
  gradient: string;
  display_seconds: number;
  max_impressions: string; // empty = unlimited
  is_active: boolean;
  sort_order: number;
  text_overlay: boolean;
  overlay_opacity: number;
  text_color: string;
};

const EMPTY_FORM: AdFormState = {
  title: "",
  eyebrow: "",
  cta_text: "",
  target_url: "",
  image_url: "",
  gradient: "",
  display_seconds: 6,
  max_impressions: "",
  is_active: true,
  sort_order: 0,
  text_overlay: false,
  overlay_opacity: 55,
  text_color: "#FFFFFF",
};

function AdminAdsPage() {
  const { t } = useTranslation();
  const { hasRole, loading: authLoading } = useAuth();
  const isOwner = hasRole("owner");

  const [ads, setAds] = useState<SidebarAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SidebarAd | null>(null);
  const [form, setForm] = useState<AdFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SidebarAd | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) {
        toast.error(
          t("admin.ads.validation.imageType", {
            defaultValue: "File must be an image (PNG, JPG, WEBP).",
          }),
        );
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        toast.error(
          t("admin.ads.validation.imageSize", {
            defaultValue: "Image must be under 2 MB.",
          }),
        );
        return;
      }

      // Validate dimensions
      const dims = await new Promise<{ w: number; h: number } | null>((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve({ w: img.width, h: img.height });
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(null);
        };
        img.src = url;
      });
      if (!dims) {
        toast.error(t("admin.ads.validation.imageRead", { defaultValue: "Could not read image." }));
        return;
      }
      const ratio = dims.w / dims.h;
      const targetRatio = RECOMMENDED_WIDTH / RECOMMENDED_HEIGHT;
      if (Math.abs(ratio - targetRatio) > 0.15) {
        toast.error(
          t("admin.ads.validation.imageRatio", {
            defaultValue: "Image must be 16:9 (recommended {{w}}x{{h}}px). Yours: {{aw}}x{{ah}}px.",
            w: RECOMMENDED_WIDTH,
            h: RECOMMENDED_HEIGHT,
            aw: dims.w,
            ah: dims.h,
          }),
        );
        return;
      }

      setUploading(true);
      const ext = file.name.split(".").pop()?.toLowerCase() || "png";
      const path = `ads/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("sidebar-ads")
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) {
        setUploading(false);
        toast.error(upErr.message);
        return;
      }
      const { data: pub } = supabase.storage.from("sidebar-ads").getPublicUrl(path);
      setForm((f) => ({ ...f, image_url: pub.publicUrl }));
      setUploading(false);
      toast.success(t("admin.ads.toasts.imageUploaded", { defaultValue: "Image uploaded" }));
    },
    [t],
  );

  const loadAds = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("sidebar_ads")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) {
      toast.error(t("admin.ads.toasts.loadError", { defaultValue: "Failed to load ads" }));
    } else {
      setAds((data ?? []) as SidebarAd[]);
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    if (isOwner) loadAds();
  }, [isOwner, loadAds]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (ad: SidebarAd) => {
    setEditing(ad);
    setForm({
      title: ad.title,
      eyebrow: ad.eyebrow ?? "",
      cta_text: ad.cta_text ?? "",
      target_url: ad.target_url,
      image_url: ad.image_url ?? "",
      gradient: ad.gradient ?? "",
      display_seconds: ad.display_seconds,
      max_impressions: ad.max_impressions != null ? String(ad.max_impressions) : "",
      is_active: ad.is_active,
      sort_order: ad.sort_order,
      text_overlay: ad.text_overlay ?? false,
      overlay_opacity: ad.overlay_opacity ?? 55,
      text_color: ad.text_color ?? "#FFFFFF",
    });
    setDialogOpen(true);
  };

  const openDuplicate = (ad: SidebarAd) => {
    setEditing(null);
    const suffix = t("admin.ads.duplicateSuffix", { defaultValue: " (copy)" });
    setForm({
      title: `${ad.title}${suffix}`,
      eyebrow: ad.eyebrow ?? "",
      cta_text: ad.cta_text ?? "",
      target_url: ad.target_url,
      image_url: ad.image_url ?? "",
      gradient: ad.gradient ?? "",
      display_seconds: ad.display_seconds,
      max_impressions: ad.max_impressions != null ? String(ad.max_impressions) : "",
      is_active: ad.is_active,
      sort_order: ad.sort_order,
      text_overlay: ad.text_overlay ?? false,
      overlay_opacity: ad.overlay_opacity ?? 55,
      text_color: ad.text_color ?? "#FFFFFF",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // Validation
    if (!form.title.trim()) {
      toast.error(t("admin.ads.validation.titleRequired", { defaultValue: "Title is required" }));
      return;
    }
    if (!form.target_url.trim() || !/^https?:\/\//i.test(form.target_url.trim())) {
      toast.error(
        t("admin.ads.validation.urlInvalid", {
          defaultValue: "Target URL must start with http(s)://",
        }),
      );
      return;
    }
    if (form.display_seconds < 1 || form.display_seconds > 120) {
      toast.error(
        t("admin.ads.validation.secondsRange", {
          defaultValue: "Seconds must be between 1 and 120",
        }),
      );
      return;
    }
    let maxImp: number | null = null;
    if (form.max_impressions.trim()) {
      const n = parseInt(form.max_impressions, 10);
      if (Number.isNaN(n) || n < 1) {
        toast.error(
          t("admin.ads.validation.maxImpInvalid", {
            defaultValue: "Max impressions must be a positive integer",
          }),
        );
        return;
      }
      maxImp = n;
    }

    setSaving(true);

    // Translate ad copy via Lovable AI (best-effort)
    let translations: {
      en?: { title?: string; eyebrow?: string; cta_text?: string };
      es?: { title?: string; eyebrow?: string; cta_text?: string };
    } = {};
    try {
      const { data: tData, error: tError } = await supabase.functions.invoke("translate-ad", {
        body: {
          title: form.title.trim(),
          eyebrow: form.eyebrow.trim(),
          cta_text: form.cta_text.trim(),
        },
      });
      if (tError) {
        console.warn("translate-ad failed:", tError);
        toast.warning(
          t("admin.ads.toasts.translationFailed", {
            defaultValue: "Translation skipped (will use Portuguese fallback)",
          }),
        );
      } else if (tData) {
        translations = tData;
      }
    } catch (e) {
      console.warn("translate-ad threw:", e);
    }

    const payload = {
      title: form.title.trim(),
      eyebrow: form.eyebrow.trim() || null,
      cta_text: form.cta_text.trim() || null,
      title_en: translations.en?.title || null,
      eyebrow_en: translations.en?.eyebrow || null,
      cta_text_en: translations.en?.cta_text || null,
      title_es: translations.es?.title || null,
      eyebrow_es: translations.es?.eyebrow || null,
      cta_text_es: translations.es?.cta_text || null,
      target_url: form.target_url.trim(),
      image_url: form.image_url.trim() || null,
      gradient: form.gradient.trim() || null,
      display_seconds: form.display_seconds,
      max_impressions: maxImp,
      is_active: form.is_active,
      sort_order: form.sort_order,
      text_overlay: form.text_overlay,
      overlay_opacity: form.overlay_opacity,
      text_color: form.text_color,
    };

    const { error } = editing
      ? await supabase.from("sidebar_ads").update(payload).eq("id", editing.id)
      : await supabase.from("sidebar_ads").insert(payload);

    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(
      editing
        ? t("admin.ads.toasts.updated", { defaultValue: "Ad updated" })
        : t("admin.ads.toasts.created", { defaultValue: "Ad created" }),
    );
    setDialogOpen(false);
    loadAds();
  };

  const handleToggle = async (ad: SidebarAd) => {
    const { error } = await supabase
      .from("sidebar_ads")
      .update({ is_active: !ad.is_active })
      .eq("id", ad.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    loadAds();
  };

  const handleResetCounter = async (ad: SidebarAd) => {
    const { error } = await supabase
      .from("sidebar_ads")
      .update({ impressions_count: 0 })
      .eq("id", ad.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("admin.ads.toasts.counterReset", { defaultValue: "Counter reset" }));
    loadAds();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error } = await supabase.from("sidebar_ads").delete().eq("id", deleteTarget.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("admin.ads.toasts.deleted", { defaultValue: "Ad deleted" }));
    setDeleteTarget(null);
    loadAds();
  };

  const stats = useMemo(() => {
    const active = ads.filter((a) => a.is_active).length;
    const totalImp = ads.reduce((s, a) => s + a.impressions_count, 0);
    const totalClicks = ads.reduce((s, a) => s + (a.clicks_count ?? 0), 0);
    return { total: ads.length, active, totalImp, totalClicks };
  }, [ads]);

  if (authLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!isOwner) return <Navigate to="/dashboard" />;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Sparkles className="h-6 w-6 text-primary" />
            {t("admin.ads.title", { defaultValue: "Sidebar Ads" })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("admin.ads.subtitle", {
              defaultValue: "Manage rotating promotional cards shown in every user's sidebar.",
            })}
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          {t("admin.ads.actions.new", { defaultValue: "New ad" })}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              {t("admin.ads.stats.total", { defaultValue: "Total ads" })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              {t("admin.ads.stats.active", { defaultValue: "Active" })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              {t("admin.ads.stats.impressions", { defaultValue: "Total impressions" })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalImp.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              {t("admin.ads.stats.clicks", { defaultValue: "Total clicks" })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalClicks.toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : ads.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {t("admin.ads.empty", { defaultValue: "No ads yet. Create your first one." })}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("admin.ads.table.status", { defaultValue: "Status" })}</TableHead>
                  <TableHead>{t("admin.ads.table.ad", { defaultValue: "Ad" })}</TableHead>
                  <TableHead>{t("admin.ads.table.url", { defaultValue: "URL" })}</TableHead>
                  <TableHead className="text-right">
                    {t("admin.ads.table.duration", { defaultValue: "Duration" })}
                  </TableHead>
                  <TableHead>
                    {t("admin.ads.table.impressions", { defaultValue: "Impressions" })}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("admin.ads.table.clicks", { defaultValue: "Clicks" })}
                  </TableHead>
                  <TableHead className="w-[1%] text-right">
                    {t("admin.ads.table.actions", { defaultValue: "Actions" })}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ads.map((ad) => {
                  const pct = ad.max_impressions
                    ? Math.min(100, (ad.impressions_count / ad.max_impressions) * 100)
                    : null;
                  return (
                    <TableRow key={ad.id}>
                      <TableCell>
                        <Badge variant={ad.is_active ? "default" : "secondary"}>
                          {ad.is_active
                            ? t("admin.ads.status.active", { defaultValue: "Active" })
                            : t("admin.ads.status.paused", { defaultValue: "Paused" })}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[280px]">
                        {ad.eyebrow && (
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {ad.eyebrow}
                          </div>
                        )}
                        <div className="font-medium">{ad.title}</div>
                        {ad.cta_text && (
                          <div className="text-xs text-muted-foreground">{ad.cta_text}</div>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <a
                          href={ad.target_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 truncate text-xs text-primary hover:underline"
                        >
                          {ad.target_url}
                          <ExternalLink className="h-3 w-3 shrink-0" />
                        </a>
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {ad.display_seconds}s
                      </TableCell>
                      <TableCell className="min-w-[180px]">
                        <div className="text-xs tabular-nums">
                          {ad.impressions_count.toLocaleString()}
                          {" / "}
                          {ad.max_impressions
                            ? ad.max_impressions.toLocaleString()
                            : t("admin.ads.unlimited", { defaultValue: "∞" })}
                        </div>
                        {pct != null && <Progress value={pct} className="mt-1 h-1.5" />}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {(ad.clicks_count ?? 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggle(ad)}
                            title={
                              ad.is_active
                                ? t("admin.ads.actions.pause", { defaultValue: "Pause" })
                                : t("admin.ads.actions.activate", { defaultValue: "Activate" })
                            }
                          >
                            <Switch checked={ad.is_active} className="pointer-events-none" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleResetCounter(ad)}
                            title={t("admin.ads.actions.resetCounter", {
                              defaultValue: "Reset counter",
                            })}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDuplicate(ad)}
                            title={t("admin.ads.actions.duplicate", { defaultValue: "Duplicate" })}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(ad)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(ad)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing
                ? t("admin.ads.dialog.editTitle", { defaultValue: "Edit ad" })
                : t("admin.ads.dialog.createTitle", { defaultValue: "New ad" })}
            </DialogTitle>
            <DialogDescription>
              {t("admin.ads.dialog.description", {
                defaultValue:
                  "Configure the message, link and how often this ad is shown to users.",
              })}
            </DialogDescription>
          </DialogHeader>

          <AiGenerator
            onApply={(out) =>
              setForm((f) => ({
                ...f,
                eyebrow: out.eyebrow ?? f.eyebrow,
                title: out.title ?? f.title,
                cta_text: out.cta_text ?? f.cta_text,
                gradient: out.gradient ?? f.gradient,
              }))
            }
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>
                {t("admin.ads.fields.eyebrow", { defaultValue: "Eyebrow (small label)" })}
              </Label>
              <Input
                value={form.eyebrow}
                onChange={(e) => setForm({ ...form, eyebrow: e.target.value })}
                placeholder={t("admin.ads.placeholders.eyebrow", {
                  defaultValue: "Digital Marketing",
                })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("admin.ads.fields.title", { defaultValue: "Title *" })}</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>{t("admin.ads.fields.cta", { defaultValue: "CTA text" })}</Label>
              <Input
                value={form.cta_text}
                onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
                placeholder={t("admin.ads.placeholders.cta", {
                  defaultValue: "Discover our services →",
                })}
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>{t("admin.ads.fields.url", { defaultValue: "Target URL *" })}</Label>
              <Input
                value={form.target_url}
                onChange={(e) => setForm({ ...form, target_url: e.target.value })}
                placeholder="https://example.com"
                required
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>{t("admin.ads.fields.image", { defaultValue: "Image (optional)" })}</Label>
              <p className="text-xs text-muted-foreground">
                {t("admin.ads.fields.imageHint", {
                  defaultValue: "Recommended {{w}}×{{h}}px (16:9), max 2 MB. PNG, JPG or WEBP.",
                  w: RECOMMENDED_WIDTH,
                  h: RECOMMENDED_HEIGHT,
                })}
              </p>
              <div className="flex items-start gap-3">
                {form.image_url ? (
                  <div className="relative">
                    <img
                      src={form.image_url}
                      alt=""
                      className="h-24 w-auto rounded-md border object-cover"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -right-2 -top-2 h-6 w-6 rounded-full"
                      onClick={() => setForm({ ...form, image_url: "" })}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex h-24 w-44 items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
                    {RECOMMENDED_WIDTH}×{RECOMMENDED_HEIGHT}
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={uploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleImageUpload(f);
                      e.target.value = "";
                    }}
                  />
                  {uploading && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {t("admin.ads.fields.uploading", { defaultValue: "Uploading..." })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>
                {t("admin.ads.fields.gradient", {
                  defaultValue: "Custom CSS background (optional)",
                })}
              </Label>

              {/* Preset gallery */}
              <div className="space-y-2 rounded-md border bg-muted/30 p-2">
                <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  {t("admin.ads.fields.bgPresets", {
                    defaultValue: "Quick presets — click to apply",
                  })}
                </p>
                <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8">
                  {[
                    { label: "Brand", value: "var(--gradient-brand)" },
                    { label: "Sunset", value: "linear-gradient(135deg, #ff6a00, #ee0979)" },
                    { label: "Ocean", value: "linear-gradient(135deg, #2193b0, #6dd5ed)" },
                    { label: "Purple", value: "linear-gradient(135deg, #7c3aed, #ec4899)" },
                    { label: "Forest", value: "linear-gradient(135deg, #11998e, #38ef7d)" },
                    { label: "Night", value: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)" },
                    { label: "Peach", value: "linear-gradient(135deg, #ffecd2, #fcb69f)" },
                    { label: "Royal", value: "linear-gradient(135deg, #141e30, #243b55)" },
                    { label: "Lime", value: "linear-gradient(135deg, #a8ff78, #78ffd6)" },
                    { label: "Fire", value: "linear-gradient(135deg, #f12711, #f5af19)" },
                    { label: "Sky", value: "linear-gradient(180deg, #56ccf2, #2f80ed)" },
                    { label: "Candy", value: "linear-gradient(135deg, #ff9a9e, #fad0c4)" },
                    {
                      label: "Mint",
                      value: "radial-gradient(circle at 30% 30%, #43e97b, #38f9d7)",
                    },
                    {
                      label: "Aurora",
                      value: "conic-gradient(from 180deg at 50% 50%, #00c6ff, #0072ff, #00c6ff)",
                    },
                    { label: "Slate", value: "#1e293b" },
                    { label: "Indigo", value: "#4f46e5" },
                  ].map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => setForm({ ...form, gradient: p.value })}
                      className="group flex flex-col items-center gap-1"
                      title={p.value}
                    >
                      <span
                        className="h-10 w-full rounded-md ring-1 ring-border transition-transform group-hover:scale-105"
                        style={{ background: p.value }}
                      />
                      <span className="text-[10px] text-muted-foreground">{p.label}</span>
                    </button>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Label className="text-[11px] text-muted-foreground">
                    {t("admin.ads.fields.bgSolid", { defaultValue: "Solid color:" })}
                  </Label>
                  <input
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(form.gradient) ? form.gradient : "#4f46e5"}
                    onChange={(e) => setForm({ ...form, gradient: e.target.value })}
                    className="h-8 w-12 cursor-pointer rounded border bg-background"
                    aria-label={t("admin.ads.fields.bgSolid", { defaultValue: "Solid color:" })}
                  />
                  <GradientBuilder onApply={(css) => setForm({ ...form, gradient: css })} />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setForm({ ...form, gradient: "" })}
                  >
                    {t("admin.ads.fields.bgClear", { defaultValue: "Clear" })}
                  </Button>
                </div>
              </div>

              <Textarea
                value={form.gradient}
                onChange={(e) => setForm({ ...form, gradient: e.target.value })}
                placeholder="linear-gradient(135deg, #7c3aed, #ec4899)"
                rows={2}
              />
              <p className="text-[11px] text-muted-foreground">
                {t("admin.ads.fields.bgHelp", {
                  defaultValue:
                    "Accepts any valid CSS background: solid color (#hex), linear-gradient, radial-gradient, conic-gradient, or url(...).",
                })}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>{t("admin.ads.fields.seconds", { defaultValue: "Seconds per slide" })}</Label>
              <Input
                type="number"
                min={1}
                max={120}
                value={form.display_seconds}
                onChange={(e) =>
                  setForm({ ...form, display_seconds: parseInt(e.target.value, 10) || 1 })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                {t("admin.ads.fields.maxImpressions", {
                  defaultValue: "Max impressions (empty = unlimited)",
                })}
              </Label>
              <Input
                type="number"
                min={1}
                value={form.max_impressions}
                onChange={(e) => setForm({ ...form, max_impressions: e.target.value })}
                placeholder="∞"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("admin.ads.fields.sortOrder", { defaultValue: "Sort order" })}</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) =>
                  setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })
                }
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
              <Label>{t("admin.ads.fields.active", { defaultValue: "Active" })}</Label>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch
                checked={form.text_overlay}
                onCheckedChange={(v) => setForm({ ...form, text_overlay: v })}
                disabled={!form.image_url}
              />
              <Label className={!form.image_url ? "text-muted-foreground" : ""}>
                {t("admin.ads.fields.textOverlay", {
                  defaultValue: "Text on top of image",
                })}
              </Label>
            </div>
            {form.text_overlay && form.image_url && (
              <div className="space-y-3 pt-4">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={form.overlay_opacity > 0}
                    onCheckedChange={(v) => setForm({ ...form, overlay_opacity: v ? 55 : 0 })}
                  />
                  <Label>
                    {t("admin.ads.fields.showOverlayBg", {
                      defaultValue: "Dark background behind text",
                    })}
                  </Label>
                </div>
                {form.overlay_opacity > 0 && (
                  <div>
                    <Label className="mb-2 block">
                      {t("admin.ads.fields.overlayOpacity", {
                        defaultValue: "Overlay darkness",
                      })}{" "}
                      — {form.overlay_opacity}%
                    </Label>
                    <Slider
                      min={0}
                      max={100}
                      step={5}
                      value={[form.overlay_opacity]}
                      onValueChange={([v]) => setForm({ ...form, overlay_opacity: v })}
                    />
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Label>
                    {t("admin.ads.fields.textColor", {
                      defaultValue: "Text color",
                    })}
                  </Label>
                  <input
                    type="color"
                    value={form.text_color}
                    onChange={(e) => setForm({ ...form, text_color: e.target.value })}
                    className="h-8 w-10 cursor-pointer rounded border border-input bg-transparent p-0.5"
                  />
                  <span className="text-xs text-muted-foreground">{form.text_color}</span>
                </div>
              </div>
            )}
          </div>

          {/* Live preview */}
          <div className="rounded-md border border-dashed p-3">
            <div className="mb-2 text-xs font-medium text-muted-foreground">
              {t("admin.ads.dialog.preview", { defaultValue: "Preview" })}
            </div>
            <div
              className="relative overflow-hidden rounded-lg text-xs shadow-sm ring-1 ring-border"
              style={{ background: form.gradient || "var(--gradient-brand)" }}
            >
              {form.image_url && (
                <img src={form.image_url} alt="" className="aspect-video w-full object-cover" />
              )}
              {form.image_url && form.text_overlay ? (
                <div className="absolute inset-0 flex items-center justify-center p-3">
                  <div
                    className={
                      form.overlay_opacity > 0
                        ? "max-w-[90%] rounded-md px-3 py-2 text-center backdrop-blur-sm"
                        : "max-w-[90%] px-3 py-2 text-center"
                    }
                    style={
                      form.overlay_opacity > 0
                        ? { background: `rgb(0 0 0 / ${form.overlay_opacity}%)` }
                        : undefined
                    }
                  >
                    {form.eyebrow && (
                      <p
                        className="text-[10px] font-semibold uppercase tracking-wider"
                        style={{ color: form.text_color, opacity: 0.8 }}
                      >
                        {form.eyebrow}
                      </p>
                    )}
                    <p className="mt-0.5 font-semibold" style={{ color: form.text_color }}>
                      {form.title ||
                        t("admin.ads.dialog.titlePlaceholder", { defaultValue: "Your title" })}
                    </p>
                    {form.cta_text && (
                      <p
                        className="mt-1 text-[11px]"
                        style={{ color: form.text_color, opacity: 0.9 }}
                      >
                        {form.cta_text}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-3">
                  {form.eyebrow && (
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-foreground/80">
                      {form.eyebrow}
                    </p>
                  )}
                  <p className="mt-0.5 font-semibold text-primary-foreground">
                    {form.title ||
                      t("admin.ads.dialog.titlePlaceholder", { defaultValue: "Your title" })}
                  </p>
                  {form.cta_text && (
                    <p className="mt-1 text-[11px] text-primary-foreground/80">{form.cta_text}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("common.save", { defaultValue: "Save" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("admin.ads.delete.title", { defaultValue: "Delete this ad?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.ads.delete.description", {
                defaultValue: "This cannot be undone.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", { defaultValue: "Cancel" })}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              {t("common.delete", { defaultValue: "Delete" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function GradientBuilder({ onApply }: { onApply: (css: string) => void }) {
  const { t } = useTranslation();
  const [from, setFrom] = useState("#7c3aed");
  const [to, setTo] = useState("#ec4899");
  const [angle, setAngle] = useState(135);
  const css = `linear-gradient(${angle}deg, ${from}, ${to})`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          {t("admin.ads.fields.bgGradientHelper", {
            defaultValue: "Build a 2-color gradient",
          })}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3">
        <div className="h-12 w-full rounded-md ring-1 ring-border" style={{ background: css }} />
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs">
            {t("admin.ads.fields.bgFrom", { defaultValue: "From" })}
          </Label>
          <input
            type="color"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-8 w-12 cursor-pointer rounded border bg-background"
          />
          <Label className="text-xs">{t("admin.ads.fields.bgTo", { defaultValue: "To" })}</Label>
          <input
            type="color"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-8 w-12 cursor-pointer rounded border bg-background"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">
            {t("admin.ads.fields.bgAngle", { defaultValue: "Angle" })}: {angle}°
          </Label>
          <Slider
            min={0}
            max={360}
            step={1}
            value={[angle]}
            onValueChange={(v) => setAngle(v[0] ?? 0)}
          />
        </div>
        <Button type="button" size="sm" className="w-full" onClick={() => onApply(css)}>
          {t("admin.ads.fields.bgApply", { defaultValue: "Apply" })}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

type AiGeneratorOutput = {
  eyebrow?: string;
  title?: string;
  cta_text?: string;
  gradient?: string;
};

function AiGenerator({ onApply }: { onApply: (out: AiGeneratorOutput) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!description.trim()) {
      toast.error(
        t("admin.ads.ai.descriptionRequired", {
          defaultValue: "Descreva o anúncio para a IA gerar.",
        }),
      );
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-ad", {
        body: { description },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      onApply({
        eyebrow: data?.eyebrow,
        title: data?.title,
        cta_text: data?.cta_text,
        gradient: data?.gradient,
      });
      toast.success(t("admin.ads.ai.success", { defaultValue: "Campos preenchidos pela IA" }));
      setOpen(false);
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : t("admin.ads.ai.error", { defaultValue: "Falha ao gerar com IA" }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-4 rounded-md border border-dashed bg-muted/30 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <p className="text-sm font-medium">
            {t("admin.ads.ai.title", { defaultValue: "Preencher com IA" })}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant={open ? "ghost" : "outline"}
          onClick={() => setOpen((v) => !v)}
        >
          {open
            ? t("admin.ads.ai.hide", { defaultValue: "Fechar" })
            : t("admin.ads.ai.open", { defaultValue: "Usar IA" })}
        </Button>
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder={t("admin.ads.ai.placeholder", {
              defaultValue:
                "Ex.: promova nosso curso de marketing digital com foco em donos de pequenos negócios; tom motivador, urgência leve.",
            })}
            disabled={loading}
          />
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              onClick={generate}
              disabled={loading || !description.trim()}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                  {t("admin.ads.ai.generating", { defaultValue: "Gerando..." })}
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-3 w-3" />
                  {t("admin.ads.ai.generate", { defaultValue: "Gerar" })}
                </>
              )}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {t("admin.ads.ai.hint", {
              defaultValue:
                "A IA preenche eyebrow, título, CTA e sugere um background. Você pode editar depois. As traduções EN/ES são geradas automaticamente ao salvar.",
            })}
          </p>
        </div>
      )}
    </div>
  );
}
